import React from "react";
import {
  Box,
  Typography,
  Container,
  Card,
  CardContent,
  CardActions,
  Button,
  Grid,
  Chip,
  Stack,
  Divider,
  useTheme,
  useMediaQuery,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DownloadIcon from "@mui/icons-material/Download";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import DescriptionIcon from "@mui/icons-material/Description";
import FolderIcon from "@mui/icons-material/Folder";

// Datos de los UFVs (un PDF por año)
const ufvData = [
  {
    year: 2025,
    fileName: "UfV 2025.pdf",
    fileLink: "./preguntasFrecuentesMD/ufv_2025.pdf",
    date: "31/12/2025",
  },
  {
    year: 2024,
    fileName: "UfV 2024.pdf",
    fileLink: "./preguntasFrecuentesMD/ufv_2024.pdf",
    date: "31/12/2024",
  },
  {
    year: 2023,
    fileName: "UfV 2023.pdf",
    fileLink: "./preguntasFrecuentesMD/ufv_2023.pdf",
    date: "31/12/2023",
  },
  {
    year: 2022,
    fileName: "UfV 2022.pdf",
    fileLink: "./preguntasFrecuentesMD/ufv_2022.pdf",
    date: "31/12/2022",
  },
  {
    year: 2021,
    fileName: "UfV 2021.pdf",
    fileLink: "./preguntasFrecuentesMD/ufv_2021.pdf",
    date: "31/12/2021",
  },
];

const UFVsPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleDownload = (fileLink,fileName) => {
    // Lógica para descargar el archivo
    const link = document.createElement("a");
    link.href = `/${fileLink}`; // Ajusta esta ruta según tu estructura de archivos
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 5 }}>
      <Card elevation={3}>
        <CardContent>
          <Stack
            direction="row"
            alignItems="center"
            spacing={2}
            mb={3}
          >
            <FolderIcon color="primary" sx={{ fontSize: 40 }} />
            <Box>
              <Typography
                variant="h3"
                fontWeight="bold"
                color="primary"
              >
                UFVs
              </Typography>
              <Typography variant="subtitle1" color="text.secondary">
                Unidad de Fomento a la Vivienda - Archivos Anuales
                2021-2025
              </Typography>
            </Box>
          </Stack>

          <Paper
            elevation={0}
            sx={{
              p: 3,
              mb: 4,
              backgroundColor: "#e3f2fd",
              borderLeft: "4px solid #1976d2",
            }}
          >
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              📋 Instrucciones de uso:
            </Typography>
            <Box component="ul" sx={{ pl: 3 }}>
              <li>Selecciona el año que deseas consultar</li>
              <li>
                Haz clic en el botón de descarga para obtener el
                archivo PDF anual
              </li>
              <li>
                Cada archivo contiene todos los valores UFV del año
                correspondiente
              </li>
            </Box>
          </Paper>

          <Divider sx={{ my: 3 }} />

          <Grid container spacing={3}>
            {ufvData.map((ufv) => (
              <Grid item xs={12} sm={6} md={4} key={ufv.year}>
                <Card
                  elevation={2}
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    transition: "transform 0.2s, box-shadow 0.2s",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: 6,
                    },
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, p: 3 }}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={2}
                      mb={2}
                    >
                      <PictureAsPdfIcon
                        sx={{
                          color: "#d32f2f",
                          fontSize: 48,
                        }}
                      />
                      <Box>
                        <Typography
                          variant="h5"
                          fontWeight="bold"
                          color="primary"
                        >
                          {ufv.year}
                        </Typography>
                        <Chip
                          label="Documento PDF"
                          size="small"
                          color="error"
                          variant="outlined"
                        />
                      </Box>
                    </Stack>

                    <Divider sx={{ my: 2 }} />

                    <Stack spacing={1} mb={2}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <DescriptionIcon
                          sx={{
                            color: "text.secondary",
                            fontSize: 20,
                          }}
                        />
                        <Typography
                          variant="body1"
                          fontWeight="medium"
                          sx={{
                            wordBreak: "break-word",
                            hyphens: "auto",
                          }}
                        >
                          {ufv.fileName}
                        </Typography>
                      </Box>

                      <Box display="flex" alignItems="center" gap={1}>
                        <CalendarTodayIcon
                          sx={{
                            color: "text.secondary",
                            fontSize: 20,
                          }}
                        />
                        <Typography
                          variant="body2"
                          color="text.secondary"
                        >
                          Actualizado: {ufv.date}
                        </Typography>
                      </Box>
                    </Stack>

                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        backgroundColor: "#f5f5f5",
                        borderRadius: 1,
                      }}
                    >
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        textAlign="center"
                      >
                        📄 Documento completo con todos los valores
                        UFV del año
                      </Typography>
                    </Paper>
                  </CardContent>

                  <CardActions sx={{ p: 3, pt: 0 }}>
                    <Button
                      fullWidth
                      variant="contained"
                      size="large"
                      startIcon={<DownloadIcon />}
                      onClick={() => handleDownload(ufv.fileLink,ufv.fileName)}
                      sx={{
                        backgroundColor: "#d32f2f",
                        "&:hover": {
                          backgroundColor: "#b71c1c",
                        },
                        py: 1.5,
                      }}
                    >
                      {isMobile
                        ? "Descargar"
                        : `Descargar ${ufv.year}`}
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Divider sx={{ my: 4 }} />

          <Paper
            elevation={0}
            sx={{
              p: 3,
              backgroundColor: "#fff3e0",
              border: "1px solid #ffcc80",
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              mb={2}
            >
              <DescriptionIcon color="warning" />
              <Typography
                variant="h6"
                fontWeight="bold"
                color="warning.main"
              >
                Información Importante
              </Typography>
            </Stack>
            <Typography variant="body1" paragraph>
              Los valores de UFV (Unidad de Fomento a la Vivienda)
              publicados aquí son documentos oficiales emitidos por la
              autoridad competente. Cada archivo contiene todos los
              valores mensuales del año correspondiente.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              ⚠️ Los archivos se actualizan anualmente. Última
              actualización: {new Date().toLocaleDateString()}
            </Typography>
          </Paper>
        </CardContent>
      </Card>
    </Container>
  );
};

export default UFVsPage;
